import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tab, Tabs, Grid, IconButton, Typography } from '@mui/material';
import useModuleAccess from '../../hooks/useModuleAccess';
import { useSearchParams } from 'react-router-dom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GroupCategories from './GroupCategories';
import PrimaryGroups from './PrimaryGroups';
import SubGroups from './SubGroups';
import Ledgers from './Ledgers';
import GSTSettlement from '../Taxes/GSTSettlement';
import SalaryList from '../Salary/SalaryList';
import CostCentreManagement from '../CostCentre/CostCentreManagement';
import StatutoryCompliance from '../Taxes/StatutoryCompliance';
import ExpenseSettlement from './ExpenseSettlement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: string;
  value: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coa-tabpanel-${index}`}
      aria-labelledby={`coa-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: string) {
  return {
    id: `coa-tab-${index}`,
    'aria-controls': `coa-tabpanel-${index}`,
  };
}

// Each tab maps 1:1 to a COA sub-module in the backend module registry, so a
// user only ever sees the tabs their grants cover. Keyed by sub-module rather
// than by position — hiding a tab must not shift the others' indices.
const TAB_DEFS: { key: string; label: string; render: () => React.ReactNode }[] = [
  { key: 'group-categories', label: 'Group Categories', render: () => <GroupCategories /> },
  { key: 'primary-groups', label: 'Primary Groups', render: () => <PrimaryGroups /> },
  { key: 'sub-groups', label: 'Sub Groups', render: () => <SubGroups /> },
  { key: 'ledgers', label: 'Ledgers', render: () => <Ledgers /> },
  { key: 'tax-settlement', label: 'Tax Settlement', render: () => <GSTSettlement /> },
  {
    key: 'expense-settlement',
    label: 'Expense Settlement',
    render: () => (
      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3 } }}>
        <ExpenseSettlement />
      </Box>
    ),
  },
  { key: 'statutory-compliance', label: 'Statutory Compliance', render: () => <StatutoryCompliance /> },
  { key: 'cost-centres', label: 'Cost Centres', render: () => <CostCentreManagement /> },
];

// ?tab= values used by links from elsewhere in the app.
const TAB_KEY_BY_PARAM: Record<string, string> = {
  taxes: 'tax-settlement',
  'expense-settlement': 'expense-settlement',
  'statutory-compliance': 'statutory-compliance',
  'cost-centres': 'cost-centres',
};

const ChartsOfAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { can } = useModuleAccess();

  const visibleTabs = useMemo(
    () => TAB_DEFS.filter(t => can('coa', 'view', t.key)),
    [can]
  );

  const [selected, setSelected] = useState('');

  // Falls back to the first tab the user can actually see, so MUI never gets a
  // `value` with no matching Tab (e.g. on first render, or if grants change).
  const value = visibleTabs.some(t => t.key === selected)
    ? selected
    : visibleTabs[0]?.key ?? '';

  // Check if we need to switch tabs based on URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const key = tabParam ? TAB_KEY_BY_PARAM[tabParam] : undefined;
    if (key && visibleTabs.some(t => t.key === key)) {
      setSelected(key);
    }
  }, [searchParams, visibleTabs]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelected(newValue);
  };

  if (!visibleTabs.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: '#666' }}>
          You do not have permission to view any Chart of Accounts section.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Sticky Tabs Section - Outside main container */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Charts of Account tabs"
          sx={{
            px: 1,
            '& .MuiTabs-indicator': {
              backgroundColor: '#059669',
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#545454',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#059669',
                fontWeight: 600,
              },
              '&:hover': {
                color: '#059669',
                backgroundColor: 'rgba(5, 150, 105, 0.04)',
              },
            },
          }}
        >
          {visibleTabs.map(t => (
            <Tab key={t.key} value={t.key} label={t.label} {...a11yProps(t.key)} />
          ))}
        </Tabs>
      </Box>

      {/* Scrollable Content Section */}
      <Box sx={{ px: 1 }}>
        <Box sx={{ bgcolor: '#fff', minHeight: '70vh' }}>
          {visibleTabs.map(t => (
            <TabPanel key={t.key} value={value} index={t.key}>
              {t.render()}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default ChartsOfAccount;
